import React, { useState, useEffect, useRef } from 'react';
import './Grafik2.css';
import rotationsAPI from '../../api/rotations';

function Grafik2({ projects = [], users = [], currentUser, contracts = {} }) {
  const [selectedProject, setSelectedProject] = useState(projects.length > 0 ? projects[0] : '');
  const [selectedRotation, setSelectedRotation] = useState('all');
  const [viewMode, setViewMode] = useState('week'); // 'week' lub 'month' - domyślnie tygodniowy
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [weekStartDate, setWeekStartDate] = useState(() => {
    // Oblicz poniedziałek bieżącego tygodnia
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Dostosuj do poniedziałku
    const monday = new Date(today.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  const [hoursData, setHoursData] = useState({}); // { dateKey: { employeeId: { hours: number, absence: string } } }
  const [rotations, setRotations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [showOvertimeSplitModal, setShowOvertimeSplitModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null); // { date: string, employeeId: string }
  const [selectedCells, setSelectedCells] = useState(new Set()); // Set<string> - "dateKey-employeeId"
  const selectedCellsRef = React.useRef(new Set()); // Ref do przechowywania aktualnych wartości
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null); // { date: string, employeeId: string }
  const [editingHours, setEditingHours] = useState(null); // { date: string, employeeId: string }
  const [overtimeSplitData, setOvertimeSplitData] = useState({ daily: '', night: '' }); // { daily: number, night: number }
  const [pendingHours, setPendingHours] = useState(null); // Liczba godzin oczekująca na podział nadgodzin

  // Nazwy miesięcy po polsku
  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];

  // Nazwy dni tygodnia po polsku
  const dayNames = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'So', 'Nie'];

  // Typy absencji zgodne z polskim prawem pracy
  const absenceTypes = {
    'urlop': { label: 'Urlop', color: '#3b82f6', icon: '🏖️', bgColor: '#dbeafe' },
    'urlop_na_zadanie': { label: 'Urlop na żądanie', color: '#60a5fa', icon: '🏝️', bgColor: '#dbeafe' },
    'chorobowe': { label: 'Zwolnienie lekarskie', color: '#f59e0b', icon: '🏥', bgColor: '#fef3c7' },
    'opieka': { label: 'Opieka nad dzieckiem', color: '#ec4899', icon: '👶', bgColor: '#fce7f3' },
    'okolicznosciowy': { label: 'Urlop okolicznościowy', color: '#8b5cf6', icon: '🎉', bgColor: '#ede9fe' },
    'bezplatny': { label: 'Urlop bezpłatny', color: '#6b7280', icon: '📅', bgColor: '#f3f4f6' },
    'swieto': { label: 'Święto', color: '#ef4444', icon: '🎊', bgColor: '#fee2e2' },
    'delegacja': { label: 'Delegacja', color: '#10b981', icon: '✈️', bgColor: '#d1fae5' }
  };

  // Wczytaj rotacje dla wybranego projektu
  useEffect(() => {
    if (!selectedProject) {
      setRotations([]);
      setSelectedRotation('all');
      return;
    }

    const loadRotations = async () => {
      try {
        const rotationsData = await rotationsAPI.getByProject(selectedProject);
        const formattedRotations = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          pattern: r.pattern || [],
          daysPerCycle: r.daysPerCycle || 9,
          project: r.project || selectedProject
        }));
        setRotations(formattedRotations);
      } catch (error) {
        console.error('Błąd ładowania rotacji:', error);
        setRotations([]);
      }
    };

    loadRotations();
    
    // Nasłuchuj aktualizacji rotacji
    const handleRotationsUpdate = () => {
      loadRotations();
    };
    window.addEventListener('rotationsUpdated', handleRotationsUpdate);
    
    return () => {
      window.removeEventListener('rotationsUpdated', handleRotationsUpdate);
    };
  }, [selectedProject]);

  // Wczytaj zmiany dla wybranego projektu
  useEffect(() => {
    if (!selectedProject) {
      setShifts([]);
      return;
    }

    const loadShifts = () => {
      const key = `workShifts_${selectedProject}`;
      const savedShifts = localStorage.getItem(key);
      if (savedShifts) {
        const parsed = JSON.parse(savedShifts);
        setShifts(parsed);
      } else {
        setShifts([]);
      }
    };

    loadShifts();

    // Nasłuchuj zmian w zmianach
    const handleShiftsUpdate = () => {
      loadShifts();
    };

    window.addEventListener('shiftsUpdated', handleShiftsUpdate);
    return () => {
      window.removeEventListener('shiftsUpdated', handleShiftsUpdate);
    };
  }, [selectedProject]);

  // Funkcja sortująca pracowników: Lider zawsze na początku, potem alfabetycznie po stanowisku
  const sortEmployees = (employeesList) => {
    return [...employeesList].sort((a, b) => {
      const positionA = (a.position || '').toLowerCase();
      const positionB = (b.position || '').toLowerCase();
      
      // Lider zawsze na początku
      const isLiderA = positionA === 'lider';
      const isLiderB = positionB === 'lider';
      
      if (isLiderA && !isLiderB) return -1;
      if (!isLiderA && isLiderB) return 1;
      
      // Jeśli obaj są liderami lub żaden nie jest liderem, sortuj alfabetycznie po stanowisku
      if (positionA < positionB) return -1;
      if (positionA > positionB) return 1;
      
      // Jeśli stanowiska są takie same, sortuj po imieniu i nazwisku
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || '';
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.name || '';
      return nameA.localeCompare(nameB, 'pl');
    });
  };

  // Filtruj pracowników przypisanych do wybranego projektu
  useEffect(() => {
    if (!selectedProject || !users) {
      setEmployees([]);
      return;
    }

    // Jeśli wybrano "Wszystkie zmiany", pokaż wszystkich pracowników z projektu
    if (selectedRotation === 'all') {
      const filteredEmployees = users.filter(user => {
        if (user.role !== 'employee') return false;
        const hasProject = user.projects && user.projects.includes(selectedProject);
        return hasProject;
      });
      const sortedEmployees = sortEmployees(filteredEmployees);
      setEmployees(sortedEmployees);
      return;
    }

    // Filtruj pracowników dla wybranej rotacji
    const rotation = rotations.find(r => String(r.id) === String(selectedRotation));
    if (!rotation) {
      setEmployees([]);
      return;
    }

    const filteredEmployees = users.filter(user => {
      if (user.role !== 'employee') return false;
      const hasProject = user.projects && user.projects.includes(selectedProject);
      if (!hasProject) return false;

      const userRotationId = user.rotationId ? String(user.rotationId) : null;
      const userShift = user.shift || '';
      const matchesRotation = userRotationId === String(selectedRotation) || 
                              userShift === rotation.title;
      return matchesRotation;
    });

    const sortedEmployees = sortEmployees(filteredEmployees);
    setEmployees(sortedEmployees);
  }, [selectedProject, selectedRotation, users, rotations]);

  // Automatyczne przechodzenie na kolejny tydzień (tylko dla widoku tygodniowego)
  // UWAGA: Ten efekt działa tylko przy pierwszym załadowaniu lub zmianie widoku
  // Nie nadpisuje ręcznych zmian użytkownika
  useEffect(() => {
    if (viewMode !== 'week') return;

    const checkAndUpdateWeek = () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Oblicz poniedziałek bieżącego tygodnia
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      const currentWeekMonday = new Date(today);
      currentWeekMonday.setDate(today.getDate() - day + (day === 0 ? -6 : 1));
      currentWeekMonday.setHours(0, 0, 0, 0);
      
      // Porównaj daty (tylko data, bez czasu)
      const currentWeekMondayTime = currentWeekMonday.getTime();
      const weekStartDateTime = weekStartDate.getTime();
      
      // Jeśli aktualny tydzień jest inny niż wyświetlany, przejdź na niego
      // Ale tylko jeśli użytkownik nie przegląda przyszłych tygodni
      if (currentWeekMondayTime !== weekStartDateTime && weekStartDateTime < currentWeekMondayTime) {
        setWeekStartDate(new Date(currentWeekMonday));
        // Zaktualizuj też miesiąc i rok jeśli potrzeba
        setCurrentMonth(currentWeekMonday.getMonth());
        setCurrentYear(currentWeekMonday.getFullYear());
      }
    };

    // Sprawdź przy załadowaniu
    checkAndUpdateWeek();

    // Sprawdź co 5 minut (zmniejszono częstotliwość, aby nie przeszkadzać użytkownikowi)
    const interval = setInterval(checkAndUpdateWeek, 300000);

    return () => clearInterval(interval);
  }, [viewMode]); // Usunięto weekStartDate z zależności, aby nie resetować ręcznych zmian

  // Wczytaj dane godzin i absencji
  useEffect(() => {
    if (!selectedProject) {
      setHoursData({});
      return;
    }

    const loadData = () => {
      // Dla widoku tygodniowego, wczytaj dane z całego miesiąca (ponieważ tydzień może być na granicy miesięcy)
      const monthToLoad = viewMode === 'week' ? weekStartDate.getMonth() : currentMonth;
      const yearToLoad = viewMode === 'week' ? weekStartDate.getFullYear() : currentYear;
      
      const key = `hoursData_${selectedProject}_${yearToLoad}_${monthToLoad}`;
      const savedData = localStorage.getItem(key);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        // Upewnij się, że wszystkie komórki mają pole overtime
        Object.keys(parsed).forEach(dateKey => {
          Object.keys(parsed[dateKey]).forEach(empId => {
            if (!parsed[dateKey][empId].overtime) {
              parsed[dateKey][empId].overtime = 0;
            }
          });
        });
        setHoursData(parsed);
      } else {
        setHoursData({});
      }
    };

    loadData();

    // Nasłuchuj zmian w localStorage (gdy dane są aktualizowane w innej zakładce)
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith(`hoursData_${selectedProject}_`)) {
        loadData();
      }
    };

    // Nasłuchuj custom event dla zmian w tej samej zakładce
    const handleDataUpdate = () => {
      loadData();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('hoursDataUpdated', handleDataUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('hoursDataUpdated', handleDataUpdate);
    };
  }, [selectedProject, currentMonth, currentYear, viewMode, weekStartDate]);

  // Przelicz nadgodziny gdy employees lub rotations się zmienią
  useEffect(() => {
    if (employees.length === 0 || Object.keys(hoursData).length === 0 || rotations.length === 0) return;
    
    const recalculated = { ...hoursData };
    let hasChanges = false;
    
    Object.keys(recalculated).forEach(dateKey => {
      Object.keys(recalculated[dateKey]).forEach(empId => {
        const cellData = recalculated[dateKey][empId];
        if (cellData && cellData.hours && cellData.hours !== '') {
          const overtime = calculateOvertime(dateKey, empId, cellData.hours);
          const currentOvertime = recalculated[dateKey][empId].overtime;
          // Porównaj nadgodziny (sprawdź czy struktura się zmieniła)
          if (!currentOvertime || 
              (typeof currentOvertime === 'object' && currentOvertime.total !== overtime.total) ||
              (typeof currentOvertime === 'number' && currentOvertime !== overtime.total)) {
            recalculated[dateKey][empId].overtime = overtime;
            hasChanges = true;
          }
        } else if (cellData && (!cellData.overtime || cellData.overtime === undefined)) {
          recalculated[dateKey][empId].overtime = { daily: 0, weekly: 0, weekend: 0, secondShift: 0, total: 0, type: 'normal' };
          hasChanges = true;
        }
      });
    });
    
    if (hasChanges) {
      setHoursData(recalculated);
      // Zapisz przeliczone dane
      if (selectedProject) {
        const monthToSave = viewMode === 'week' ? weekStartDate.getMonth() : currentMonth;
        const yearToSave = viewMode === 'week' ? weekStartDate.getFullYear() : currentYear;
        const key = `hoursData_${selectedProject}_${yearToSave}_${monthToSave}`;
        localStorage.setItem(key, JSON.stringify(recalculated));
        window.dispatchEvent(new CustomEvent('hoursDataUpdated'));
      }
    }
  }, [employees.length, rotations.length, selectedProject, viewMode, currentMonth, currentYear, weekStartDate]);

  // Zapisz dane godzin i absencji
  const saveHoursData = (data) => {
    if (!selectedProject) return;
    
    // Dla widoku tygodniowego, zapisz do miesiąca, w którym jest poniedziałek tygodnia
    const monthToSave = viewMode === 'week' ? weekStartDate.getMonth() : currentMonth;
    const yearToSave = viewMode === 'week' ? weekStartDate.getFullYear() : currentYear;
    
    const key = `hoursData_${selectedProject}_${yearToSave}_${monthToSave}`;
    
    // Wczytaj istniejące dane dla tego miesiąca
    const existingData = localStorage.getItem(key);
    let mergedData = existingData ? JSON.parse(existingData) : {};
    
    // Scal nowe dane z istniejącymi
    Object.keys(data).forEach(dateKey => {
      if (!mergedData[dateKey]) {
        mergedData[dateKey] = {};
      }
      Object.assign(mergedData[dateKey], data[dateKey]);
    });
    
    localStorage.setItem(key, JSON.stringify(mergedData));
    setHoursData(mergedData);
    
    // Wywołaj event dla synchronizacji między zakładkami
    window.dispatchEvent(new CustomEvent('hoursDataUpdated'));
  };

  // Oblicz poniedziałek tygodnia dla danej daty
  const getMondayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Dostosuj do poniedziałku
    const monday = new Date(d.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  // Pobierz dni tygodnia (poniedziałek - niedziela)
  const getDaysInWeek = () => {
    const days = [];
    const monday = new Date(weekStartDate);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      const day = date.getDate();
      const month = date.getMonth();
      const year = date.getFullYear();
      
      days.push({
        day,
        date,
        dateKey: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        dayOfWeek: i, // 0 = poniedziałek, 6 = niedziela
        isWeekend: i === 5 || i === 6 // Sobota lub niedziela
      });
    }
    
    return days;
  };

  // Pobierz dni miesiąca
  const getDaysInMonth = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();

    const days = [];
    
    // Dni miesiąca - bez pustych komórek na początku, zawsze zaczynamy od 1. dnia miesiąca
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      days.push({
        day,
        date,
        dateKey: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        dayOfWeek: date.getDay() === 0 ? 6 : date.getDay() - 1,
        isWeekend: date.getDay() === 0 || date.getDay() === 6
      });
    }

    return days;
  };

  // Zmień tydzień
  const changeWeek = (direction) => {
    // weekStartDate jest już poniedziałkiem, więc po prostu dodajemy/odejmujemy 7 dni
    const currentDate = new Date(weekStartDate);
    const newWeekStart = new Date(currentDate);
    
    if (direction === 'prev') {
      // Przejdź do poprzedniego tygodnia (cofnij o 7 dni)
      newWeekStart.setDate(currentDate.getDate() - 7);
    } else if (direction === 'next') {
      // Przejdź do następnego tygodnia (przejdź o 7 dni do przodu)
      newWeekStart.setDate(currentDate.getDate() + 7);
    }
    
    // Upewnij się, że godziny są wyzerowane
    newWeekStart.setHours(0, 0, 0, 0);
    
    // Ustaw nową datę - używamy nowego obiektu Date, aby wymusić re-render
    const finalDate = new Date(newWeekStart);
    setWeekStartDate(finalDate);
    setCurrentMonth(finalDate.getMonth());
    setCurrentYear(finalDate.getFullYear());
  };

  // Zmień miesiąc
  const changeMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  // Formatuj zakres dat tygodnia
  const getWeekRange = () => {
    if (!weekStartDate) return '';
    
    const monday = new Date(weekStartDate);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const formatDate = (date) => {
      if (!date || isNaN(date.getTime())) return '';
      return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
    };
    
    return `${formatDate(monday)} - ${formatDate(sunday)}`;
  };

  // Sprawdź czy rotacja pracownika jest tylko od poniedziałku do piątku
  const isRotationMondayToFriday = (employee) => {
    if (!employee.rotationId || !selectedProject) return false;
    
    const rotation = rotations.find(r => String(r.id) === String(employee.rotationId));
    if (!rotation || !rotation.pattern) return false;
    
    // Sprawdź czy w rotacji są tylko dni robocze (pon-pt, indeksy 0-4)
    // Sobota i niedziela (indeksy 5-6) powinny być puste/null
    let hasWeekendWork = false;
    let hasWeekdayWork = false;
    
    rotation.pattern.forEach(day => {
      if (day.shifts && Array.isArray(day.shifts)) {
        // Sprawdź dni robocze (0-4 = pon-pt)
        for (let i = 0; i < 5; i++) {
          if (day.shifts[i] && day.shifts[i] !== 'off' && day.shifts[i] !== null) {
            hasWeekdayWork = true;
          }
        }
        // Sprawdź weekend (5-6 = sob-nie)
        for (let i = 5; i < 7; i++) {
          if (day.shifts[i] && day.shifts[i] !== 'off' && day.shifts[i] !== null) {
            hasWeekendWork = true;
          }
        }
      }
    });
    
    // Rotacja jest pon-pt jeśli ma dni robocze i NIE ma weekendu
    return hasWeekdayWork && !hasWeekendWork;
  };

  // Sprawdź czy pracownik pracuje na drugą zmianę (14:00-22:00) w danym dniu
  const isSecondShift = (employee, dateKey) => {
    if (!employee.rotationId || !selectedProject) return false;
    
    const rotation = rotations.find(r => String(r.id) === String(employee.rotationId));
    if (!rotation || !rotation.pattern) return false;
    
    // Parsuj datę z dateKey (format: YYYY-MM-DD)
    const [year, month, day] = dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0 = pon, 6 = nie
    
    // Znajdź odpowiedni dzień w rotacji (pierwszy tydzień)
    const rotationDay = rotation.pattern[dayOfWeek];
    if (!rotationDay || !rotationDay.shifts || !Array.isArray(rotationDay.shifts)) return false;
    
    // Sprawdź czy w tym dniu jest zmiana która kończy się o 22:00
    for (let i = 0; i < rotationDay.shifts.length; i++) {
      const shiftId = rotationDay.shifts[i];
      if (shiftId && shiftId !== 'off' && shiftId !== null) {
        const shift = shifts.find(s => String(s.id) === String(shiftId));
        if (shift && shift.startTime === '14:00' && shift.endTime === '22:00') {
          return true;
        }
      }
    }
    
    return false;
  };

  // Oblicz nadgodziny dla danego dnia i pracownika zgodnie z polskim prawem pracy
  const calculateOvertime = (dateKey, employeeId, hours, manualDaily = null, manualNight = null) => {
    if (!hours || hours === '' || parseFloat(hours) === 0) return { daily: 0, weekly: 0, weekend: 0, secondShift: 0, manualDaily: 0, manualNight: 0, total: 0, type: 'normal' };
    
    const hoursNum = parseFloat(hours);
    
    // Parsuj datę z dateKey (format: YYYY-MM-DD)
    const [year, month, day] = dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0 = pon, 6 = nie
    const isWeekend = dayOfWeek === 5 || dayOfWeek === 6; // Sobota lub niedziela
    const isSunday = dayOfWeek === 6; // Niedziela
    
    // Znajdź pracownika
    const employee = employees.find(emp => (emp.email || emp.id) === employeeId);
    if (!employee) return { daily: 0, weekly: 0, weekend: 0, secondShift: 0, manualDaily: 0, manualNight: 0, total: 0, type: 'normal' };
    
    // Sprawdź czy są ręcznie wprowadzone nadgodziny
    const cellData = hoursData[dateKey]?.[employeeId];
    const existingManualDaily = manualDaily !== null ? manualDaily : (cellData?.overtime?.manualDaily || null);
    const existingManualNight = manualNight !== null ? manualNight : (cellData?.overtime?.manualNight || null);
    
    let dailyOvertime = 0; // Nadgodziny dobowe (powyżej 8h)
    let weekendOvertime = 0; // Nadgodziny za pracę w weekend (dla rotacji pon-pt)
    let secondShiftOvertime = 0; // Nadgodziny za pracę po 22:00 na drugiej zmianie
    let manualDailyOvertime = 0; // Ręcznie wprowadzone nadgodziny dzienne
    let manualNightOvertime = 0; // Ręcznie wprowadzone nadgodziny nocne
    let overtimeType = 'normal'; // 'normal' (50%), 'premium' (100% - niedziela/święto/nocne)
    
    // Jeśli są ręcznie wprowadzone nadgodziny, użyj ich
    if (existingManualDaily !== null && existingManualDaily !== '' && parseFloat(existingManualDaily) >= 0) {
      manualDailyOvertime = parseFloat(existingManualDaily) || 0;
    }
    if (existingManualNight !== null && existingManualNight !== '' && parseFloat(existingManualNight) >= 0) {
      manualNightOvertime = parseFloat(existingManualNight) || 0;
      if (manualNightOvertime > 0) {
        overtimeType = 'premium'; // Nadgodziny nocne są zawsze 100%
      }
    }
    
    // Jeśli są ręcznie wprowadzone nadgodziny, użyj ich zamiast automatycznego obliczania
    if (manualDailyOvertime > 0 || manualNightOvertime > 0) {
      const totalOvertime = manualDailyOvertime + manualNightOvertime;
      return {
        daily: manualDailyOvertime,
        weekly: 0,
        weekend: 0,
        secondShift: manualNightOvertime,
        manualDaily: manualDailyOvertime,
        manualNight: manualNightOvertime,
        total: totalOvertime,
        type: overtimeType
      };
    }
    
    // Przypadek 1: Nadgodziny dobowe - powyżej 8 godzin w ciągu dnia
    if (hoursNum > 8) {
      dailyOvertime = hoursNum - 8;
    }
    
    // Przypadek 2: Jeśli rotacja jest pon-pt i pracownik pracuje w weekend
    // Zgodnie z polskim prawem pracy, praca w niedzielę/święto dla rotacji pon-pt to nadgodziny od 1. godziny
    if (isRotationMondayToFriday(employee) && isWeekend && hoursNum > 0) {
      // Wszystkie godziny w weekend to nadgodziny (od 1. godziny)
      weekendOvertime = hoursNum;
      // Jeśli to niedziela, nadgodziny są typu premium (100%)
      if (isSunday) {
        overtimeType = 'premium';
      }
    }
    
    // Przypadek 3: Jeśli pracownik pracuje na drugą zmianę (14:00-22:00) i zostaje po 22:00
    // TYLKO jeśli nie ma ręcznie wprowadzonych nadgodzin
    // (Ta logika jest teraz zastąpiona przez ręczne wprowadzanie)
    
    // Oblicz całkowite nadgodziny
    // Zgodnie z polskim prawem pracy:
    // - Nadgodziny dobowe: powyżej 8h w ciągu dnia
    // - Nadgodziny weekendowe: wszystkie godziny w weekend dla rotacji pon-pt
    // - Nadgodziny na drugiej zmianie po 22:00: godziny powyżej 8h (100%)
    // Jeśli pracownik pracuje w weekend i ma > 8h, to weekendowe nadgodziny już zawierają wszystkie godziny
    // Jeśli pracownik pracuje na drugiej zmianie i ma > 8h, to nadgodziny po 22:00 są 100%
    let finalOvertime = 0;
    if (weekendOvertime > 0) {
      // Jeśli są nadgodziny weekendowe, to one mają priorytet (zawierają wszystkie godziny)
      finalOvertime = weekendOvertime;
    } else if (secondShiftOvertime > 0) {
      // Jeśli są nadgodziny na drugiej zmianie po 22:00, to one są 100%
      finalOvertime = secondShiftOvertime;
    } else {
      // Jeśli nie ma nadgodzin weekendowych ani na drugiej zmianie, to liczymy tylko dobowe
      finalOvertime = dailyOvertime;
    }
    
    return {
      daily: dailyOvertime,
      weekly: 0, // Będzie obliczane osobno dla całego tygodnia
      weekend: weekendOvertime,
      secondShift: secondShiftOvertime,
      manualDaily: manualDailyOvertime,
      manualNight: manualNightOvertime,
      total: finalOvertime,
      type: overtimeType
    };
  };

  // Zapisz podział nadgodzin na dzienne i nocne
  const saveOvertimeSplit = (dateKey, employeeId, dailyHours, nightHours) => {
    const newData = { ...hoursData };
    if (!newData[dateKey]) {
      newData[dateKey] = {};
    }
    if (!newData[dateKey][employeeId]) {
      newData[dateKey][employeeId] = { hours: '', absence: '', overtime: { daily: 0, weekly: 0, weekend: 0, secondShift: 0, manualDaily: 0, manualNight: 0, total: 0, type: 'normal' } };
    }
    
    const dailyNum = parseFloat(dailyHours) || 0;
    const nightNum = parseFloat(nightHours) || 0;
    // Użyj pendingHours jeśli jest dostępne, w przeciwnym razie użyj zapisanych godzin
    const totalHours = pendingHours || (newData[dateKey][employeeId].hours ? parseFloat(newData[dateKey][employeeId].hours) : 0);
    
    // Upewnij się, że godziny są zapisane
    if (pendingHours) {
      newData[dateKey][employeeId].hours = pendingHours;
    }
    
    // Oblicz nadgodziny z ręcznie wprowadzonymi wartościami
    const overtime = calculateOvertime(dateKey, employeeId, totalHours, dailyNum, nightNum);
    newData[dateKey][employeeId].overtime = overtime;
    
    saveHoursData(newData);
    setShowOvertimeSplitModal(false);
    setSelectedCell(null);
    setOvertimeSplitData({ daily: '', night: '' });
    setPendingHours(null);
  };

  // Oblicz nadgodziny tygodniowe (powyżej 40h w tygodniu)
  // Zgodnie z polskim prawem pracy: nadgodziny tygodniowe to suma godzin powyżej 40h w tygodniu
  // UWAGA: Nadgodziny tygodniowe są dodatkowe do nadgodzin dobowych i weekendowych
  // Jeśli pracownik ma już nadgodziny dobowe/weekendowe, to nadgodziny tygodniowe są obliczane
  // jako różnica między całkowitą liczbą godzin a 40h, MINUS już policzone nadgodziny dobowe/weekendowe
  const calculateWeeklyOvertime = (employeeId, weekStart) => {
    if (!weekStart || viewMode !== 'week') return 0;
    
    let totalWeekHours = 0;
    let totalDailyOvertime = 0;
    let totalWeekendOvertime = 0;
    const weekDays = getDaysInWeek();
    
    weekDays.forEach(day => {
      if (day && day.dateKey) {
        const cellData = getCellData(day.dateKey, employeeId);
        if (cellData.hours) {
          totalWeekHours += parseFloat(cellData.hours) || 0;
        }
        // Zbierz już policzone nadgodziny dobowe i weekendowe
        if (cellData.overtime && typeof cellData.overtime === 'object') {
          totalDailyOvertime += parseFloat(cellData.overtime.daily || 0);
          totalWeekendOvertime += parseFloat(cellData.overtime.weekend || 0);
        }
      }
    });
    
    // Nadgodziny tygodniowe = godziny powyżej 40h MINUS już policzone nadgodziny dobowe/weekendowe
    const weeklyOvertime = Math.max(0, totalWeekHours - 40 - totalDailyOvertime - totalWeekendOvertime);
    
    return weeklyOvertime;
  };

  // Pobierz dane dla komórki
  const getCellData = (dateKey, employeeId) => {
    if (!hoursData[dateKey] || !hoursData[dateKey][employeeId]) {
      return { hours: '', absence: '', overtime: { daily: 0, weekly: 0, weekend: 0, secondShift: 0, manualDaily: 0, manualNight: 0, total: 0, type: 'normal' } };
    }
    const data = hoursData[dateKey][employeeId];
    // Oblicz nadgodziny jeśli są godziny
    if (data.hours && data.hours !== '') {
      const overtime = calculateOvertime(dateKey, employeeId, data.hours);
      return { ...data, overtime };
    }
    return { 
      ...data, 
      overtime: data.overtime || { daily: 0, weekly: 0, weekend: 0, secondShift: 0, manualDaily: 0, manualNight: 0, total: 0, type: 'normal' } 
    };
  };

  // Zaktualizuj godziny
  const updateHours = (dateKey, employeeId, hours) => {
    const newData = { ...hoursData };
    if (!newData[dateKey]) {
      newData[dateKey] = {};
    }
    if (!newData[dateKey][employeeId]) {
      newData[dateKey][employeeId] = { hours: '', absence: '', overtime: { daily: 0, weekly: 0, weekend: 0, secondShift: 0, total: 0, type: 'normal' } };
    }
    
    // Walidacja: godziny 0-24
    const hoursNum = parseFloat(hours);
    if (isNaN(hoursNum) || hoursNum < 0 || hoursNum > 24) {
      if (hours !== '' && hours !== '0') return; // Nie zapisuj nieprawidłowych wartości
    }
    
    newData[dateKey][employeeId].hours = hours === '' ? '' : hoursNum;
    
    // Jeśli wpisano więcej niż 8h, pokaż modal podziału nadgodzin
    if (hours !== '' && hoursNum > 8) {
      // Sprawdź czy to nie jest weekend (dla weekendu automatycznie przypisujemy nadgodziny)
      const [year, month, day] = dateKey.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
      const isWeekend = dayOfWeek === 5 || dayOfWeek === 6;
      
      // Znajdź pracownika
      const employee = employees.find(emp => (emp.email || emp.id) === employeeId);
      const isWeekendOvertime = employee && isRotationMondayToFriday(employee) && isWeekend;
      
      // Jeśli to nie jest weekend z automatycznymi nadgodzinami, pokaż modal podziału
      if (!isWeekendOvertime) {
        // Zapisz godziny przed pokazaniem modala
        newData[dateKey][employeeId].hours = hoursNum;
        
        setSelectedCell({ date: dateKey, employeeId });
        setPendingHours(hoursNum); // Zapisz liczbę godzin do użycia w modalu
        setOvertimeSplitData({ 
          daily: newData[dateKey][employeeId].overtime?.manualDaily || '', 
          night: newData[dateKey][employeeId].overtime?.manualNight || '' 
        });
        setShowOvertimeSplitModal(true);
        
        // Zapisz godziny (bez nadgodzin na razie)
        saveHoursData(newData);
        return; // Nie obliczaj nadgodzin jeszcze, poczekaj na potwierdzenie z modala
      }
    }
    
    // Oblicz i zapisz nadgodziny automatycznie
    if (hours !== '' && hoursNum > 0) {
      const overtime = calculateOvertime(dateKey, employeeId, hoursNum);
      newData[dateKey][employeeId].overtime = overtime;
    } else {
      newData[dateKey][employeeId].overtime = { daily: 0, weekly: 0, weekend: 0, secondShift: 0, total: 0, type: 'normal' };
    }
    
    // Usuń absencję jeśli wprowadzamy godziny
    if (hours !== '' && hoursNum > 0) {
      newData[dateKey][employeeId].absence = '';
    }
    
    saveHoursData(newData);
  };

  // Zaktualizuj absencję dla wielu komórek
  const updateAbsenceForMultiple = (dateKey, employeeId, absenceType, cellsSet) => {
    if (!cellsSet || cellsSet.size === 0) {
      return; // Brak komórek do aktualizacji
    }
    
    // Użyj funkcji aktualizującej stan, aby mieć pewność, że zmiany są zastosowane
    setHoursData(prevData => {
      const newData = { ...prevData };
      
      let updatedCount = 0;
      
      cellsSet.forEach(cellKey => {
        // Format klucza: "YYYY-MM-DD-employeeId"
        // Data zawsze ma format YYYY-MM-DD (2 myślniki), więc dzielimy na trzecim myślniku
        const parts = cellKey.split('-');
        if (parts.length < 4) {
          return; // Pomiń nieprawidłowy klucz
        }
        
        // Data to pierwsze 3 części (YYYY-MM-DD), reszta to employeeId
        const cellDateKey = `${parts[0]}-${parts[1]}-${parts[2]}`;
        const cellEmployeeId = parts.slice(3).join('-'); // Połącz resztę, bo employeeId może też zawierać myślniki
        
        if (!newData[cellDateKey]) {
          newData[cellDateKey] = {};
        }
        if (!newData[cellDateKey][cellEmployeeId]) {
          newData[cellDateKey][cellEmployeeId] = { 
            hours: '', 
            absence: '', 
            overtime: { 
              daily: 0, 
              weekly: 0, 
              weekend: 0, 
              secondShift: 0, 
              manualDaily: 0, 
              manualNight: 0, 
              total: 0, 
              type: 'normal' 
            } 
          };
        }
        
        // Zawsze ustaw absencję (nie sprawdzaj czy już jest taka sama - pozwól użytkownikowi dodać ponownie)
        const hadAbsence = newData[cellDateKey][cellEmployeeId].absence === absenceType;
        newData[cellDateKey][cellEmployeeId].absence = absenceType;
        // Usuń godziny i nadgodziny gdy dodajemy absencję
        newData[cellDateKey][cellEmployeeId].hours = '';
        newData[cellDateKey][cellEmployeeId].overtime = { 
          daily: 0, 
          weekly: 0, 
          weekend: 0, 
          secondShift: 0, 
          manualDaily: 0, 
          manualNight: 0, 
          total: 0, 
          type: 'normal' 
        };
        updatedCount++;
      });
      
      // Zapisz dane
      saveHoursData(newData);
      
      return newData;
    });
    
    setShowAbsenceModal(false);
    clearSelection();
  };

  // Zaktualizuj absencję (pojedyncza komórka)
  const updateAbsence = (dateKey, employeeId, absenceType) => {
    const newData = { ...hoursData };
    
    // Pojedyncza komórka
    if (!newData[dateKey]) {
      newData[dateKey] = {};
    }
    if (!newData[dateKey][employeeId]) {
      newData[dateKey][employeeId] = { hours: '', absence: '', overtime: { daily: 0, weekly: 0, weekend: 0, secondShift: 0, manualDaily: 0, manualNight: 0, total: 0, type: 'normal' } };
    }
    
    // Jeśli kliknięto ten sam typ absencji, usuń ją
    if (newData[dateKey][employeeId].absence === absenceType) {
      newData[dateKey][employeeId].absence = '';
    } else {
      newData[dateKey][employeeId].absence = absenceType;
      // Usuń godziny i nadgodziny gdy dodajemy absencję
      newData[dateKey][employeeId].hours = '';
      newData[dateKey][employeeId].overtime = { daily: 0, weekly: 0, weekend: 0, secondShift: 0, manualDaily: 0, manualNight: 0, total: 0, type: 'normal' };
    }
    
    saveHoursData(newData);
    setShowAbsenceModal(false);
    clearSelection();
  };

  // Otwórz modal absencji
  const openOptionModal = (dateKey, employeeId, event) => {
    // Zatrzymaj propagację, aby nie uruchamiać zaznaczania
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Upewnij się, że ref jest zawsze zsynchronizowany ze stanem
    if (selectedCells.size > 0) {
      selectedCellsRef.current = new Set(selectedCells);
    }
    
    // Jeśli jest zaznaczenie wielu komórek, użyj go (NIE czyść zaznaczenia!)
    if (selectedCells.size > 0 || selectedCellsRef.current.size > 0) {
      // NIE wywołuj clearSelection() - zachowaj zaznaczenie!
      setShowAbsenceModal(true);
      return;
    }
    // W przeciwnym razie zaznacz pojedynczą komórkę
    setSelectedCell({ date: dateKey, employeeId });
    setShowAbsenceModal(true);
  };

  // Rozpocznij zaznaczanie zakresu
  const startSelection = (dateKey, employeeId, event) => {
    // NIE uruchamiaj zaznaczania jeśli kliknięto na przycisk, input lub badge
    if (event.target.closest('button') || event.target.closest('input') || event.target.closest('.absence-badge')) {
      return;
    }
    
    // Jeśli trzymamy Shift, rozszerz zaznaczenie
    if (event.shiftKey && selectionStart) {
      extendSelection(dateKey, employeeId);
      return;
    }
    // W przeciwnym razie rozpocznij nowe zaznaczenie
    const newSelection = new Set([`${dateKey}-${employeeId}`]);
    setIsSelecting(true);
    setSelectionStart({ date: dateKey, employeeId });
    setSelectedCells(newSelection);
    selectedCellsRef.current = newSelection; // Zaktualizuj ref
    setSelectedCell(null);
  };

  // Rozszerz zaznaczenie
  const extendSelection = (dateKey, employeeId) => {
    if (!selectionStart) return;
    
    const startDate = selectionStart.date;
    const startEmployeeId = selectionStart.employeeId;
    
    // Znajdź indeksy dat i pracowników
    const days = viewMode === 'week' ? getDaysInWeek(weekStartDate) : getDaysInMonth(currentMonth, currentYear);
    const dayIndices = {};
    days.forEach((day, idx) => {
      if (day && day.dateKey) {
        dayIndices[day.dateKey] = idx;
      }
    });
    
    const employeeIndices = {};
    employees.forEach((emp, idx) => {
      const empId = emp.email || emp.id;
      employeeIndices[empId] = idx;
    });
    
    const startDayIdx = dayIndices[startDate] ?? 0;
    const endDayIdx = dayIndices[dateKey] ?? 0;
    const startEmpIdx = employeeIndices[startEmployeeId] ?? 0;
    const endEmpIdx = employeeIndices[employeeId] ?? 0;
    
    const minDayIdx = Math.min(startDayIdx, endDayIdx);
    const maxDayIdx = Math.max(startDayIdx, endDayIdx);
    const minEmpIdx = Math.min(startEmpIdx, endEmpIdx);
    const maxEmpIdx = Math.max(startEmpIdx, endEmpIdx);
    
    // Utwórz zaznaczenie prostokątne
    const newSelection = new Set();
    for (let dayIdx = minDayIdx; dayIdx <= maxDayIdx; dayIdx++) {
      const day = days[dayIdx];
      if (!day || !day.dateKey) continue;
      
      for (let empIdx = minEmpIdx; empIdx <= maxEmpIdx; empIdx++) {
        const emp = employees[empIdx];
        if (!emp) continue;
        const empId = emp.email || emp.id;
        newSelection.add(`${day.dateKey}-${empId}`);
      }
    }
    
    setSelectedCells(newSelection);
    selectedCellsRef.current = newSelection; // Zaktualizuj ref
  };

  // Zakończ zaznaczanie
  const endSelection = () => {
    setIsSelecting(false);
  };

  // Sprawdź czy komórka jest zaznaczona
  const isCellSelected = (dateKey, employeeId) => {
    return selectedCells.has(`${dateKey}-${employeeId}`);
  };

  // Sprawdź czy pracownik ma jakiekolwiek zaznaczone komórki
  const hasSelectedCells = (employeeId) => {
    return Array.from(selectedCells).some(cellKey => {
      const parts = cellKey.split('-');
      if (parts.length < 4) return false;
      const cellEmployeeId = parts.slice(3).join('-');
      return cellEmployeeId === employeeId;
    });
  };

  // Nawigacja między komórkami za pomocą strzałek
  const navigateToCell = (currentDateKey, currentEmployeeId, direction) => {
    const currentDays = viewMode === 'week' ? getDaysInWeek() : getDaysInMonth();
    const currentDayIndex = currentDays.findIndex(day => day.dateKey === currentDateKey);
    const currentEmployeeIndex = employees.findIndex(emp => (emp.email || emp.id) === currentEmployeeId);

    // Jeśli nie znaleziono aktualnej komórki, nie rób nic
    if (currentDayIndex === -1 || currentEmployeeIndex === -1) {
      return;
    }

    let newDayIndex = currentDayIndex;
    let newEmployeeIndex = currentEmployeeIndex;

    switch (direction) {
      case 'ArrowLeft':
        newDayIndex = Math.max(0, currentDayIndex - 1);
        break;
      case 'ArrowRight':
        newDayIndex = Math.min(currentDays.length - 1, currentDayIndex + 1);
        break;
      case 'ArrowUp':
        newEmployeeIndex = Math.max(0, currentEmployeeIndex - 1);
        break;
      case 'ArrowDown':
        newEmployeeIndex = Math.min(employees.length - 1, currentEmployeeIndex + 1);
        break;
      default:
        return;
    }

    // Jeśli indeksy się nie zmieniły, nie rób nic
    if (newDayIndex === currentDayIndex && newEmployeeIndex === currentEmployeeIndex) {
      return;
    }

    const newDay = currentDays[newDayIndex];
    const newEmployee = employees[newEmployeeIndex];

    if (newDay && newEmployee) {
      const newDateKey = newDay.dateKey;
      const newEmployeeId = newEmployee.email || newEmployee.id;
      
      // Ustaw nową komórkę do edycji
      setEditingHours({ date: newDateKey, employeeId: newEmployeeId });
      
      // Ustaw zaznaczenie
      const cellKey = `${newDateKey}-${newEmployeeId}`;
      setSelectedCells(new Set([cellKey]));
      selectedCellsRef.current = new Set([cellKey]);
      
      // Ustaw focus na input (z małym opóźnieniem, aby React zdążył zrenderować)
      setTimeout(() => {
        const input = document.querySelector(`input[data-date="${newDateKey}"][data-employee="${newEmployeeId}"]`);
        if (input) {
          input.focus();
          input.select();
        }
      }, 10);
    }
  };

  // Wyczyść zaznaczenie
  const clearSelection = () => {
    setSelectedCells(new Set());
    selectedCellsRef.current = new Set(); // Wyczyść ref
    setSelectionStart(null);
    setSelectedCell(null);
  };

  // Pobierz sumę godzin dla pracownika (zawsze dla całego miesiąca)
  const getTotalHours = (employeeId) => {
    let total = 0;
    const daysToCheck = getDaysInMonth();
    
    daysToCheck.forEach(day => {
      if (day && day.dateKey) {
        const cellData = getCellData(day.dateKey, employeeId);
        if (cellData.hours) {
          total += parseFloat(cellData.hours) || 0;
        }
      }
    });
    
    return total.toFixed(2);
  };

  // Pobierz sumę nadgodzin dla pracownika (zawsze dla całego miesiąca) - szczegółowo według typu
  const getTotalOvertime = (employeeId) => {
    let totalDaily = 0; // Nadgodziny dzienne (powyżej 8h w dni robocze)
    let totalNight = 0; // Nadgodziny nocne (po 22:00 na drugiej zmianie)
    let totalSaturday = 0; // Nadgodziny sobota (dla rotacji pon-pt)
    let totalSunday = 0; // Nadgodziny niedziela (dla rotacji pon-pt)
    
    const daysToCheck = getDaysInMonth();
    
    daysToCheck.forEach(day => {
      if (day && day.dateKey) {
        const cellData = getCellData(day.dateKey, employeeId);
        if (cellData.overtime && typeof cellData.overtime === 'object') {
          // Parsuj datę z dateKey (format: YYYY-MM-DD)
          const [year, month, dayNum] = day.dateKey.split('-').map(Number);
          const date = new Date(year, month - 1, dayNum);
          const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0 = pon, 6 = nie
          const isSaturday = dayOfWeek === 5;
          const isSunday = dayOfWeek === 6;
          const isWeekday = dayOfWeek >= 0 && dayOfWeek <= 4;
          
          // Nadgodziny nocne (po 22:00 na drugiej zmianie) - sprawdź ręcznie wprowadzone lub automatyczne
          if (cellData.overtime.manualNight && cellData.overtime.manualNight > 0) {
            totalNight += parseFloat(cellData.overtime.manualNight || 0);
          } else if (cellData.overtime.secondShift && cellData.overtime.secondShift > 0) {
            totalNight += parseFloat(cellData.overtime.secondShift || 0);
          }
          
          // Nadgodziny weekendowe
          if (cellData.overtime.weekend && cellData.overtime.weekend > 0) {
            if (isSaturday) {
              totalSaturday += parseFloat(cellData.overtime.weekend || 0);
            } else if (isSunday) {
              totalSunday += parseFloat(cellData.overtime.weekend || 0);
            }
          }
          
          // Nadgodziny dzienne (powyżej 8h w dni robocze) - sprawdź ręcznie wprowadzone lub automatyczne
          if (isWeekday) {
            if (cellData.overtime.manualDaily && cellData.overtime.manualDaily > 0) {
              totalDaily += parseFloat(cellData.overtime.manualDaily || 0);
            } else if (cellData.overtime.daily && cellData.overtime.daily > 0) {
              // Jeśli są nadgodziny nocne (ręczne lub automatyczne), to nie dodajemy automatycznych nadgodzin dobowych
              const hasNightOvertime = (cellData.overtime.manualNight && cellData.overtime.manualNight > 0) ||
                                      (cellData.overtime.secondShift && cellData.overtime.secondShift > 0);
              if (!hasNightOvertime) {
                totalDaily += parseFloat(cellData.overtime.daily || 0);
              }
            }
          }
        } else if (cellData.overtime) {
          // Kompatybilność wsteczna - jeśli overtime jest liczbą, traktuj jako dzienne
          const [year, month, dayNum] = day.dateKey.split('-').map(Number);
          const date = new Date(year, month - 1, dayNum);
          const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
          const isWeekday = dayOfWeek >= 0 && dayOfWeek <= 4;
          if (isWeekday) {
            totalDaily += parseFloat(cellData.overtime) || 0;
          }
        }
      }
    });
    
    // Nadgodziny tygodniowe są obliczane tylko dla całego miesiąca
    // (nie ma sensu liczyć nadgodzin tygodniowych dla pojedynczego tygodnia w kontekście miesięcznego podsumowania)
    
    const total = totalDaily + totalNight + totalSaturday + totalSunday;
    
    return {
      total: total.toFixed(2),
      daily: totalDaily.toFixed(2), // Nadgodziny dzienne
      night: totalNight.toFixed(2), // Nadgodziny nocne
      saturday: totalSaturday.toFixed(2), // Nadgodziny sobota
      sunday: totalSunday.toFixed(2) // Nadgodziny niedziela
    };
  };

  // Pobierz liczbę dni absencji dla pracownika (zawsze dla całego miesiąca)
  const getAbsenceDays = (employeeId) => {
    let count = 0;
    const daysToCheck = getDaysInMonth();
    
    daysToCheck.forEach(day => {
      if (day && day.dateKey) {
        const cellData = getCellData(day.dateKey, employeeId);
        if (cellData.absence && cellData.absence !== '') {
          count++;
        }
      }
    });
    
    return count;
  };

  // Pobierz liczbę dni dla konkretnego typu absencji (zawsze dla całego miesiąca)
  const getAbsenceDaysByType = (employeeId, absenceType) => {
    let count = 0;
    const daysToCheck = getDaysInMonth();
    
    daysToCheck.forEach(day => {
      if (day && day.dateKey) {
        const cellData = getCellData(day.dateKey, employeeId);
        if (cellData.absence === absenceType) {
          count++;
        }
      }
    });
    
    return count;
  };

  const days = viewMode === 'week' ? getDaysInWeek() : getDaysInMonth();

  if (projects.length === 0) {
    return (
      <div className="grafik2-container">
        <div className="grafik2-message">
          <p>Brak dostępnych klientów. Najpierw dodaj klienta w menu Admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grafik2-container">
      {/* Nagłówek z filtrami */}
      <div className="grafik2-header">
        <div className="grafik2-filters">
          <div className="filter-group">
            <label htmlFor="project-select">Klient:</label>
            <select
              id="project-select"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="filter-select"
            >
              {projects.map((project, idx) => (
                <option key={idx} value={project}>{project}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="rotation-select">Rotacja:</label>
            <select
              id="rotation-select"
              value={selectedRotation}
              onChange={(e) => setSelectedRotation(e.target.value)}
              className="filter-select"
            >
              <option value="all">Wszystkie zmiany</option>
              {rotations.map((rotation) => (
                <option key={rotation.id} value={rotation.id}>
                  {rotation.title} (Rota. {rotation.rotationNumber})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="view-mode-select">Widok:</label>
            <select
              id="view-mode-select"
              value={viewMode}
              onChange={(e) => {
                setViewMode(e.target.value);
                if (e.target.value === 'week') {
                  // Przy przejściu na widok tygodniowy, ustaw aktualny tydzień
                  const today = new Date();
                  const monday = getMondayOfWeek(today);
                  setWeekStartDate(monday);
                  setCurrentMonth(monday.getMonth());
                  setCurrentYear(monday.getFullYear());
                }
              }}
              className="filter-select"
            >
              <option value="week">Tygodniowy</option>
              <option value="month">Miesięczny</option>
            </select>
          </div>

          {viewMode === 'week' ? (
            <div className="filter-group">
              <label>Tydzień:</label>
              <div className="month-navigation">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    changeWeek('prev');
                  }}
                  className="month-nav-button"
                  aria-label="Poprzedni tydzień"
                  title="Poprzedni tydzień"
                >
                  ‹
                </button>
                <span className="month-display">
                  {getWeekRange()}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    changeWeek('next');
                  }}
                  className="month-nav-button"
                  aria-label="Następny tydzień"
                  title="Następny tydzień"
                >
                  ›
                </button>
              </div>
            </div>
          ) : (
            <div className="filter-group">
              <label>Miesiąc:</label>
              <div className="month-navigation">
                <button
                  onClick={() => changeMonth('prev')}
                  className="month-nav-button"
                  aria-label="Poprzedni miesiąc"
                >
                  ‹
                </button>
                <span className="month-display">
                  {monthNames[currentMonth]} {currentYear}
                </span>
                <button
                  onClick={() => changeMonth('next')}
                  className="month-nav-button"
                  aria-label="Następny miesiąc"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabela z kalendarzem */}
      {employees.length === 0 ? (
        <div className="grafik2-message">
          <p>Brak pracowników dla wybranego klienta i rotacji.</p>
        </div>
      ) : (
        <div className="grafik2-table-wrapper">
          <table className="grafik2-table">
            <thead>
              <tr>
                <th className="employee-column">Pracownik</th>
                {days.map((day) => (
                  <th
                    key={day.dateKey}
                    className={`day-header ${day.isWeekend ? 'weekend' : ''}`}
                  >
                    <div className="day-header-content">
                      <span className="day-name">{dayNames[day.dayOfWeek]}</span>
                      <span className="day-number">{day.day}</span>
                      {viewMode === 'week' && (
                        <span className="day-month">
                          {monthNames[day.date.getMonth()].substring(0, 3)}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="summary-column summary-hours-header">
                  Suma godzin
                  <span className="summary-label-small">(miesiąc)</span>
                </th>
                <th className="summary-column summary-overtime-daily-header">
                  Nadgodziny dzienne
                  <span className="summary-label-small">(miesiąc)</span>
                </th>
                <th className="summary-column summary-overtime-night-header">
                  Nadgodziny nocne
                  <span className="summary-label-small">(miesiąc)</span>
                </th>
                <th className="summary-column summary-overtime-saturday-header">
                  Nadgodziny sobota
                  <span className="summary-label-small">(miesiąc)</span>
                </th>
                <th className="summary-column summary-overtime-sunday-header">
                  Nadgodziny niedziela
                  <span className="summary-label-small">(miesiąc)</span>
                </th>
                <th className="summary-column summary-absence-header">
                  Dni absencji
                  <span className="summary-label-small">(miesiąc)</span>
                </th>
                {Object.entries(absenceTypes).map(([key, type]) => (
                  <th 
                    key={key} 
                    className="summary-column summary-absence-type-header"
                    style={{ backgroundColor: type.bgColor, color: type.color }}
                  >
                    {type.label}
                    <span className="summary-label-small">(miesiąc)</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => {
                const employeeId = employee.email || employee.id;
                const employeeName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.name || 'Nieznany';
                
                const employeeHasSelectedCells = hasSelectedCells(employeeId);
                
                return (
                  <tr key={employeeId} className="employee-row">
                    <td className={`employee-cell ${employeeHasSelectedCells ? 'employee-cell-selected' : ''}`}>
                      <div className="employee-info">
                        <span className="employee-name">{employeeName}</span>
                        <span className="employee-separator">•</span>
                        <span className="employee-position">{employee.position || '-'}</span>
                        <span className="employee-separator">•</span>
                        <span className="employee-user">{employee.systemUser || '-'}</span>
                        <span className="employee-separator">•</span>
                        <span className="employee-contract">{employee.contract || '-'}</span>
                      </div>
                    </td>
                    {days.map((day) => {
                      const cellData = getCellData(day.dateKey, employeeId);
                      const hasAbsence = cellData.absence && cellData.absence !== '';
                      const absenceInfo = hasAbsence ? absenceTypes[cellData.absence] : null;
                      
                      // Sprawdź czy są nadgodziny
                      const hasOvertime = cellData.overtime && (
                        (typeof cellData.overtime === 'object' && cellData.overtime.total > 0) ||
                        (typeof cellData.overtime === 'number' && cellData.overtime > 0)
                      );
                      const isPremiumOvertime = cellData.overtime && typeof cellData.overtime === 'object' && cellData.overtime.type === 'premium' && cellData.overtime.total > 0;
                      const isNormalOvertime = hasOvertime && !isPremiumOvertime;
                      
                      const isSelected = isCellSelected(day.dateKey, employeeId);
                      
                      return (
                        <td
                          key={day.dateKey}
                          className={`day-cell ${day.isWeekend ? 'weekend' : ''} ${hasAbsence ? 'has-absence' : ''} ${isPremiumOvertime ? 'overtime-premium-cell' : ''} ${isNormalOvertime ? 'overtime-normal-cell' : ''} ${isSelected ? 'cell-selected' : ''}`}
                          style={hasAbsence && absenceInfo ? { backgroundColor: absenceInfo.bgColor } : {}}
                        >
                          <div 
                            className="cell-content"
                            onMouseDown={(e) => {
                              // Nie uruchamiaj zaznaczania jeśli kliknięto na przycisk, input lub badge
                              if (e.button === 0 && !e.target.closest('button') && !e.target.closest('input') && !e.target.closest('.absence-badge')) {
                                startSelection(day.dateKey, employeeId, e);
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (isSelecting && selectionStart && e.buttons === 1) {
                                extendSelection(day.dateKey, employeeId);
                              }
                            }}
                            onMouseUp={() => {
                              if (isSelecting) {
                                endSelection();
                              }
                            }}
                          >
                            {editingHours?.date === day.dateKey && editingHours?.employeeId === employeeId ? (
                              <input
                                type="number"
                                min="0"
                                max="24"
                                step="0.5"
                                value={cellData.hours || ''}
                                onChange={(e) => updateHours(day.dateKey, employeeId, e.target.value)}
                                onFocus={() => {
                                  // Ustaw zaznaczenie komórki, aby podświetlić wiersz pracownika
                                  const cellKey = `${day.dateKey}-${employeeId}`;
                                  setSelectedCells(new Set([cellKey]));
                                  selectedCellsRef.current = new Set([cellKey]);
                                }}
                                onBlur={() => setEditingHours(null)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    setEditingHours(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingHours(null);
                                  } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    navigateToCell(day.dateKey, employeeId, e.key);
                                  }
                                }}
                                data-date={day.dateKey}
                                data-employee={employeeId}
                                className="hours-input"
                                autoFocus
                              />
                            ) : (
                              <div className="cell-actions">
                                {hasAbsence ? (
                                  <div
                                    className="absence-badge"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openOptionModal(day.dateKey, employeeId, e);
                                    }}
                                    title={absenceInfo?.label || ''}
                                  >
                                    <span className="absence-icon">{absenceInfo?.icon || ''}</span>
                                  </div>
                                ) : (
                                  <>
                                    <div className="hours-display-wrapper">
                                      <div
                                        className="hours-display"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingHours({ date: day.dateKey, employeeId });
                                          // Ustaw zaznaczenie komórki, aby podświetlić wiersz pracownika
                                          const cellKey = `${day.dateKey}-${employeeId}`;
                                          setSelectedCells(new Set([cellKey]));
                                          selectedCellsRef.current = new Set([cellKey]);
                                        }}
                                        title={hasOvertime ? `Nadgodziny: ${typeof cellData.overtime === 'object' ? cellData.overtime.total : cellData.overtime}h (${isPremiumOvertime ? '100%' : '50%'})` : ''}
                                      >
                                        {cellData.hours ? `${cellData.hours}` : '-'}
                                      </div>
                                    </div>
                                    <button
                                      className="absence-button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openOptionModal(day.dateKey, employeeId, e);
                                      }}
                                      title="Dodaj nadgodziny lub absencję"
                                    >
                                      +
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="summary-cell hours-summary">
                      {getTotalHours(employeeId)}h
                    </td>
                    <td className="summary-cell overtime-summary overtime-daily-summary">
                      {getTotalOvertime(employeeId).daily}h
                    </td>
                    <td className="summary-cell overtime-summary overtime-night-summary">
                      {getTotalOvertime(employeeId).night}h
                    </td>
                    <td className="summary-cell overtime-summary overtime-saturday-summary">
                      {getTotalOvertime(employeeId).saturday}h
                    </td>
                    <td className="summary-cell overtime-summary overtime-sunday-summary">
                      {getTotalOvertime(employeeId).sunday}h
                    </td>
                    <td className="summary-cell absence-summary">
                      {getAbsenceDays(employeeId)}
                    </td>
                    {Object.entries(absenceTypes).map(([key, type]) => (
                      <td 
                        key={key}
                        className="summary-cell absence-type-summary"
                        style={{ backgroundColor: type.bgColor, color: type.color }}
                      >
                        {getAbsenceDaysByType(employeeId, key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}


      {/* Modal wyboru absencji */}
      {showAbsenceModal && (selectedCell || selectedCells.size > 0) && (
        <div className="modal-overlay" onClick={() => { setShowAbsenceModal(false); clearSelection(); }}>
          <div className="modal-content absence-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Wybierz typ absencji{selectedCells.size > 0 ? ` (${selectedCells.size} komórek)` : ''}</h3>
              <button
                className="modal-close"
                onClick={() => { setShowAbsenceModal(false); clearSelection(); }}
              >
                ×
              </button>
            </div>
            <div className="absence-types-grid">
              {Object.entries(absenceTypes).map(([key, type]) => (
                <button
                  key={key}
                  className="absence-type-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    
                    // Upewnij się, że ref jest zsynchronizowany przed użyciem - użyj aktualnego stanu!
                    const currentSelectedCells = selectedCells.size > 0 ? new Set(selectedCells) : null;
                    const currentSelectedCell = selectedCell;
                    
                    // Zsynchronizuj ref dla przyszłości
                    if (selectedCells.size > 0) {
                      selectedCellsRef.current = new Set(selectedCells);
                    }
                    
                    if (currentSelectedCells && currentSelectedCells.size > 0) {
                      // Użyj pierwszej komórki z zaznaczenia jako referencji
                      const firstCell = Array.from(currentSelectedCells)[0];
                      // Format: "YYYY-MM-DD-employeeId" - dzielimy na trzecim myślniku
                      const parts = firstCell.split('-');
                      const dateKey = parts.length >= 3 ? `${parts[0]}-${parts[1]}-${parts[2]}` : parts[0];
                      const employeeId = parts.length >= 4 ? parts.slice(3).join('-') : (parts[1] || '');
                      // Przekaż zaznaczenie bezpośrednio do funkcji
                      updateAbsenceForMultiple(dateKey, employeeId, key, currentSelectedCells);
                    } else if (currentSelectedCell) {
                      updateAbsence(currentSelectedCell.date, currentSelectedCell.employeeId, key);
                    }
                  }}
                  style={{
                    backgroundColor: type.bgColor,
                    borderColor: type.color,
                    color: type.color
                  }}
                >
                  <span className="absence-type-icon">{type.icon}</span>
                  <span className="absence-type-label">{type.label}</span>
                </button>
              ))}
              <button
                className="absence-type-button remove-absence"
                onClick={() => {
                  // Zapisz zaznaczenie przed wywołaniem funkcji (closure problem)
                  const cellsToUpdate = selectedCells.size > 0 ? new Set(selectedCells) : null;
                  const singleCell = selectedCell;
                  
                  const newData = { ...hoursData };
                  
                  // Jeśli jest zaznaczenie wielu komórek, usuń absencję ze wszystkich
                  if (cellsToUpdate && cellsToUpdate.size > 0) {
                    cellsToUpdate.forEach(cellKey => {
                      // Format: "YYYY-MM-DD-employeeId" - dzielimy na trzecim myślniku
                      const parts = cellKey.split('-');
                      if (parts.length >= 4) {
                        const cellDateKey = `${parts[0]}-${parts[1]}-${parts[2]}`;
                        const cellEmployeeId = parts.slice(3).join('-');
                        if (newData[cellDateKey] && newData[cellDateKey][cellEmployeeId]) {
                          newData[cellDateKey][cellEmployeeId].absence = '';
                        }
                      }
                    });
                  } else if (singleCell) {
                    if (newData[singleCell.date] && newData[singleCell.date][singleCell.employeeId]) {
                      newData[singleCell.date][singleCell.employeeId].absence = '';
                    }
                  }
                  
                  saveHoursData(newData);
                  setShowAbsenceModal(false);
                  clearSelection();
                }}
              >
                <span className="absence-type-icon">✕</span>
                <span className="absence-type-label">Usuń absencję</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal podziału nadgodzin na dzienne i nocne */}
      {showOvertimeSplitModal && selectedCell && (
        <div className="modal-overlay" onClick={() => { 
          // Przywróć poprzednie godziny jeśli anulowano
          if (pendingHours) {
            const newData = { ...hoursData };
            if (newData[selectedCell.date] && newData[selectedCell.date][selectedCell.employeeId]) {
              // Przywróć poprzednie godziny (przed wpisaniem > 8h)
              const prevHours = newData[selectedCell.date][selectedCell.employeeId].hours;
              if (parseFloat(prevHours) === pendingHours) {
                // Jeśli godziny są takie same jak pendingHours, usuń je (użytkownik anulował)
                newData[selectedCell.date][selectedCell.employeeId].hours = '';
                newData[selectedCell.date][selectedCell.employeeId].overtime = { daily: 0, weekly: 0, weekend: 0, secondShift: 0, manualDaily: 0, manualNight: 0, total: 0, type: 'normal' };
                saveHoursData(newData);
              }
            }
          }
          setShowOvertimeSplitModal(false); 
          setSelectedCell(null); 
          setOvertimeSplitData({ daily: '', night: '' }); 
          setPendingHours(null);
        }}>
          <div className="modal-content overtime-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Podziel nadgodziny</h3>
              <button
                className="modal-close"
                onClick={() => { setShowOvertimeSplitModal(false); setSelectedCell(null); setOvertimeSplitData({ daily: '', night: '' }); }}
              >
                ×
              </button>
            </div>
            <div className="overtime-modal-content">
               <p className="overtime-modal-instruction">
                 Wprowadź podział nadgodzin. Suma nadgodzin dziennych i nocnych powinna być równa {(() => {
                   const totalHours = pendingHours || (() => {
                     const cellData = getCellData(selectedCell.date, selectedCell.employeeId);
                     return cellData.hours ? parseFloat(cellData.hours) : 0;
                   })();
                   return Math.max(0, totalHours - 8).toFixed(1);
                 })()}h (powyżej 8h pracy).
               </p>
               <div className="overtime-split-inputs">
                 <div className="overtime-input-group">
                   <label htmlFor="overtime-daily-input">Nadgodziny dzienne (50%):</label>
                   <input
                     type="number"
                     min="0"
                     max="24"
                     step="0.5"
                     placeholder="0"
                     className="overtime-input"
                     id="overtime-daily-input"
                     value={overtimeSplitData.daily}
                     onChange={(e) => setOvertimeSplitData({ ...overtimeSplitData, daily: e.target.value })}
                     autoFocus
                   />
                   <span className="overtime-unit">godz.</span>
                 </div>
                 <div className="overtime-input-group">
                   <label htmlFor="overtime-night-input">Nadgodziny nocne (100%):</label>
                   <input
                     type="number"
                     min="0"
                     max="24"
                     step="0.5"
                     placeholder="0"
                     className="overtime-input"
                     id="overtime-night-input"
                     value={overtimeSplitData.night}
                     onChange={(e) => setOvertimeSplitData({ ...overtimeSplitData, night: e.target.value })}
                   />
                   <span className="overtime-unit">godz.</span>
                 </div>
               </div>
               {(() => {
                 const totalHours = pendingHours || (() => {
                   const cellData = getCellData(selectedCell.date, selectedCell.employeeId);
                   return cellData.hours ? parseFloat(cellData.hours) : 0;
                 })();
                 const maxOvertime = Math.max(0, totalHours - 8);
                 const dailyNum = parseFloat(overtimeSplitData.daily) || 0;
                 const nightNum = parseFloat(overtimeSplitData.night) || 0;
                 const totalOvertime = dailyNum + nightNum;
                 const isValid = totalOvertime <= maxOvertime && totalOvertime >= 0 && totalOvertime > 0;
                 const errorMessage = totalOvertime > maxOvertime 
                   ? `Suma nadgodzin (${totalOvertime.toFixed(1)}h) nie może przekraczać ${maxOvertime.toFixed(1)}h`
                   : totalOvertime === 0 && maxOvertime > 0
                   ? `Wprowadź podział nadgodzin (łącznie ${maxOvertime.toFixed(1)}h)`
                   : '';
                 
                 return (
                   <>
                     {errorMessage && (
                       <div className="overtime-error-message" style={{ color: '#dc2626', fontSize: '14px', marginBottom: '12px', textAlign: 'center' }}>
                         {errorMessage}
                       </div>
                     )}
                     <div className="modal-actions">
                       <button
                         className="btn-cancel"
                         onClick={() => { 
                           setShowOvertimeSplitModal(false); 
                           setSelectedCell(null); 
                           setOvertimeSplitData({ daily: '', night: '' }); 
                           setPendingHours(null);
                         }}
                       >
                         Anuluj
                       </button>
                       <button
                         className="btn-primary"
                         disabled={!isValid}
                         onClick={() => {
                           if (isValid) {
                             saveOvertimeSplit(selectedCell.date, selectedCell.employeeId, dailyNum, nightNum);
                           }
                         }}
                         style={{ 
                           opacity: isValid ? 1 : 0.5, 
                           cursor: isValid ? 'pointer' : 'not-allowed' 
                         }}
                       >
                         Zapisz
                       </button>
                     </div>
                   </>
                 );
               })()}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Grafik2;

